import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Link,
  Alert,
  Button,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import LoadingOverlay from '../components/LoadingOverlay';
import EnhancedSearchBar from '../components/EnhancedSearchBar';
import { placeholderImage, getBestItemImageUrl } from '../utils/imageUtils';
import api from '../services/api';
import boxService from '../services/boxService';
import { getCachedSearch, saveSearchToCache } from '../utils/searchCache';
import demoService from '../services/demoService';
// Online-only mode - no longer using offlineStorage
import OfflineImage from '../components/OfflineImage';
import ItemDetailModal from '../components/ItemDetailModal';
import BoxDetailModal from '../components/BoxDetailModal';
import BoxThumbnail from '../components/BoxThumbnail';

const FindItems = () => {
  const navigate = useAppNavigation();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [boxes, setBoxes] = useState([]);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search related states
  const [searchTier, setSearchTier] = useState('keyword');
  const [searchTime, setSearchTime] = useState(null);
  const [showAiSearchOption, setShowAiSearchOption] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [urlSearchProcessed, setUrlSearchProcessed] = useState(false);
  const [activeTagSearch, setActiveTagSearch] = useState(null); // Tag being searched (for UI indicator)

  // Item detail modal state
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [itemDetailOpen, setItemDetailOpen] = useState(false);
  
  // Box detail modal state
  const [selectedBoxId, setSelectedBoxId] = useState(null);
  const [boxDetailOpen, setBoxDetailOpen] = useState(false);

  // In items section, group by shared image so identical photos appear once
  const imageGroups = useMemo(() => {
    if (!items || items.length === 0) return [];
    const map = new Map();

    items.forEach((item) => {
      const key = item.shared_image_id || item.image_url || item.id;
      if (!map.has(key)) {
        map.set(key, {
          key,
          primaryItem: item,
        });
      }
    });

    return Array.from(map.values());
  }, [items]);

  const itemsToRender = imageGroups.map((g) => g.primaryItem);

  // Check if offline data is available when we're offline
  useEffect(() => {
    const ensureOfflineData = async () => {
      if (!navigator.onLine) {
        console.log('FindItems: Device is offline - checking for available offline data...');
        await boxService.checkOfflineData();
      }
    };
    
    ensureOfflineData();
  }, []);

  // Function to fetch data that can be called both on mount and when returning to this page
  // Refactored for online-only operation (no offline storage fallback)
  const fetchData = useCallback(async (forceRefresh = false, boxesOnly = false) => {
    console.log(`FindItems: Fetching data${forceRefresh ? ' (FORCE REFRESH)' : ''}${boxesOnly ? ' (BOXES ONLY)' : ''}...`);
    try {
      setIsLoading(true);
      setError(null); // Reset any previous errors
      
      // Start with empty arrays
      let boxesData = [];
      let itemsData = [];
      
      // Add cache busting timestamp to prevent caching
      const timestamp = new Date().getTime();
      
      try {
        // Get boxes with their items
        const boxResponse = await api.get(`/boxes?_t=${timestamp}`);
        
        if (boxResponse.data && Array.isArray(boxResponse.data)) {
          boxesData = boxResponse.data;
          console.log(`FindItems: Fetched ${boxesData.length} boxes from API`);
        } else {
          console.warn('FindItems: Unexpected box response format:', boxResponse.data);
        }
      } catch (boxError) {
        console.error('FindItems: Error fetching boxes:', boxError);
        // Continue with empty boxes array
      }
      
      if (!boxesOnly) {
        try {
          // Get all items
          const itemResponse = await api.get(`/items?_t=${timestamp}`);
          
          if (itemResponse.data && Array.isArray(itemResponse.data)) {
            itemsData = itemResponse.data;
            console.log(`FindItems: Fetched ${itemsData.length} items from API`);
          } else {
            console.warn('FindItems: Unexpected item response format:', itemResponse.data);
          }
        } catch (itemError) {
          console.error('FindItems: Error fetching items:', itemError);
          // Continue with empty items array
        }
      } else {
        console.log('FindItems: Skipping items fetch (boxes only mode)');
      }
      
      // Process and set the data
      console.log(`FindItems: Processing ${boxesData.length} boxes and ${itemsData.length} items`);
      
      // Set the data
      setBoxes(boxesData);
      if (!boxesOnly) {
        setItems(itemsData);
      }
      
      console.log('FindItems: Data fetch completed successfully');
      
    } catch (error) {
      // Catch-all for any unhandled errors
      console.error('FindItems: Unhandled error in fetchData:', error);
      setError('An unexpected error occurred: ' + error.message);
      setBoxes([]);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data when component mounts (but not if we have a search query in URL)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const queryParam = urlParams.get('q');
    const tagSearchParam = urlParams.get('tagSearch');
    
    if ((!queryParam || !queryParam.trim()) && (!tagSearchParam || !tagSearchParam.trim())) {
      console.log('FindItems: Component mounted, fetching data...');
      fetchData();
    } else {
      console.log('FindItems: Component mounted with search query, skipping initial data fetch');
      // Don't fetch anything - the URL search useEffect will handle cache restoration or search
      // Fetching boxes here would overwrite cached search results
    }
  }, [fetchData, location.search]);

  // Reset URL search processed flag when search params change
  useEffect(() => {
    setUrlSearchProcessed(false);
  }, [location.search]);

  // Check for URL query parameter and auto-search
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const queryParam = urlParams.get('q');
    const tagSearchParam = urlParams.get('tagSearch');
    
    console.log('FindItems: URL search useEffect - queryParam:', queryParam, 'tagSearchParam:', tagSearchParam, 'urlSearchProcessed:', urlSearchProcessed);
    
    // Handle tag-specific search (from Tag Dashboard)
    if (tagSearchParam && tagSearchParam.trim() && !urlSearchProcessed) {
      console.log('FindItems: Found TAG search in URL, executing tag search:', tagSearchParam);
      setSearchQuery(tagSearchParam);
      setActiveTagSearch(tagSearchParam);
      setUrlSearchProcessed(true);
      
      (async () => {
        try {
          setIsSearching(true);
          setIsLoading(true);
          setShowAiSearchOption(false);
          setLastSearchQuery(tagSearchParam);
          
          const startTime = performance.now();
          
          // Use tag-specific search endpoint (exact match on indexed ItemTag table)
          const endpoint = `/search/tags?tag=${encodeURIComponent(tagSearchParam)}&limit=50`;
          console.log(`FindItems: Using tag search endpoint: ${endpoint}`);
          
          const response = await api.get(endpoint);
          console.log('FindItems: Tag search response:', response.data);
          
          const endTime = performance.now();
          const duration = Math.round(endTime - startTime);
          setSearchTime(duration);
          setSearchTier('tag-index');
          
          const searchResults = response.data?.items || [];
          console.log(`FindItems: Tag search found ${searchResults.length} items`);
          
          setItems(searchResults);
          
          // Process boxes for tag search results
          if (searchResults.length > 0) {
            const boxIdsInSearch = [...new Set(searchResults.map(item => item.box_id).filter(Boolean))];
            
            try {
              const boxResponse = await api.get('/boxes');
              const allBoxes = boxResponse.data || [];
              
              const matchingBoxes = boxIdsInSearch.map(boxId => {
                const existingBox = allBoxes.find(b => b.id === boxId);
                if (existingBox) {
                  const matchingItems = searchResults.filter(item => item.box_id === boxId);
                  return {
                    ...existingBox,
                    searchItems: matchingItems,
                    searchItemsCount: matchingItems.length
                  };
                }
                return null;
              }).filter(Boolean);
              
              setBoxes(matchingBoxes);
            } catch (boxError) {
              console.error('FindItems: Error fetching boxes for tag search:', boxError);
              setBoxes([]);
            }
          } else {
            setBoxes([]);
          }
        } catch (error) {
          console.error('FindItems: Tag search error:', error);
          setError('Failed to search by tag. Please try again.');
        } finally {
          setIsSearching(false);
          setIsLoading(false);
        }
      })();
      return;
    }
    
    // Clear tag search indicator if not a tag search
    if (!tagSearchParam) {
      setActiveTagSearch(null);
    }
    
    if (queryParam && queryParam.trim() && !urlSearchProcessed) {
      console.log('FindItems: Found search query in URL, executing search IMMEDIATELY:', queryParam);
      setSearchQuery(queryParam);
      setUrlSearchProcessed(true); // Mark as processed to prevent infinite loop
      
      // Check cache first before making API call
      // getCachedSearch will automatically return null if cache was invalidated
      const cachedResults = getCachedSearch(queryParam);
      if (cachedResults) {
        console.log('FindItems: ✅ Using cached results for URL search');
        console.log('FindItems: Cached boxes:', cachedResults.boxes.map(b => ({
          id: b.id,
          name: b.name,
          searchItemsCount: b.searchItemsCount
        })));
        setItems(cachedResults.items);
        setBoxes(cachedResults.boxes);
        setSearchTier(cachedResults.searchTier);
        setSearchTime(cachedResults.searchTime);
        setLastSearchQuery(queryParam);
        setShowAiSearchOption(true);
        setIsLoading(false);
        return;
      }
      
      // IMPORTANT: Execute search immediately without setTimeout
      (async () => {
        console.log('FindItems: IIFE - Executing search with query:', queryParam);
        
        try {
          setIsSearching(true);
          setIsLoading(true);
          setShowAiSearchOption(false);
          setLastSearchQuery(queryParam);
          
          // Record start time for performance metrics
          const startTime = performance.now();
          
          // Use enhanced hybrid search for all searches
          const endpoint = `/search?q=${encodeURIComponent(queryParam)}`;
          console.log(`FindItems: Using enhanced hybrid search endpoint: ${endpoint}`);
          
          const response = await api.get(endpoint);
          console.log('FindItems: Search response:', response.data);
          
          // Calculate search execution time
          const endTime = performance.now();
          const duration = Math.round(endTime - startTime);
          
          // Extract search metrics from enhanced hybrid search response
          const searchMetrics = response.data?.search_metrics || {};
          const backendTime = searchMetrics.total_time_ms || duration;
          setSearchTime(Math.round(backendTime));
          setSearchTier(searchMetrics.method_used || 'hybrid');
          
          // Process search results
          let searchResults = [];
          
          // Handle different response formats
          if (response.data) {
            if (Array.isArray(response.data)) {
              // Direct array response
              searchResults = response.data;
            } else if (typeof response.data === 'object' && Array.isArray(response.data.items)) {
              // Object with items array
              searchResults = response.data.items;
            }
          }
          
          console.log(`FindItems: Search found ${searchResults.length} items`);
          
          // Set items from search results
          setItems(searchResults);
          
          // Always show AI search option after basic search
          setShowAiSearchOption(true);
          
          // Declare matchingBoxes at function scope so it's available for caching
          let matchingBoxes = [];
          
          // Process boxes for search results
          if (searchResults.length > 0) {
            const boxIdsInSearch = [];
            searchResults.forEach(item => {
              if (item.box_id && !boxIdsInSearch.includes(item.box_id)) {
                boxIdsInSearch.push(item.box_id);
              }
            });
            
            console.log('FindItems: Unique box IDs in search results:', boxIdsInSearch);
            
            // Filter boxes that match the search results
            matchingBoxes = [];
            
            // If we don't have box data loaded yet, fetch it
            if (boxes.length === 0 && boxIdsInSearch.length > 0) {
              console.log('FindItems: No boxes loaded yet, fetching box data for search results...');
              try {
                const boxResponse = await api.get('/boxes');
                const allBoxes = boxResponse.data || [];
                
                // Filter boxes that match the search results
                boxIdsInSearch.forEach(boxId => {
                  const existingBox = allBoxes.find(b => b.id === boxId);
                  
                  if (existingBox) {
                    // Count matching items in this box
                    const matchingItems = searchResults.filter(item => item.box_id === boxId);
                    
                    // Add the box with updated counts
                    matchingBoxes.push({
                      ...existingBox,
                      searchItems: matchingItems,
                      searchItemsCount: matchingItems.length
                    });
                  }
                });
              } catch (boxError) {
                console.error('FindItems: Error fetching boxes for search:', boxError);
              }
            } else {
              // Use existing boxes data
              boxIdsInSearch.forEach(boxId => {
                // Try to find the box in existing boxes array
                const existingBox = boxes.find(b => b.id === boxId);
                
                if (existingBox) {
                  // Count matching items in this box
                  const matchingItems = searchResults.filter(item => item.box_id === boxId);
                  
                  // Add the box with updated counts
                  matchingBoxes.push({
                    ...existingBox,
                    searchItems: matchingItems,
                    searchItemsCount: matchingItems.length
                  });
                }
              });
            }
            
            console.log(`FindItems: Found ${matchingBoxes.length} boxes containing search matches`);
            setBoxes(matchingBoxes);
          } else {
            // If no search results, show no boxes
            console.log('FindItems: No search results found, showing no boxes');
            setBoxes([]);
          }
          
          // Save successful search to cache
          saveSearchToCache(queryParam, {
            items: searchResults,
            boxes: matchingBoxes.length > 0 ? matchingBoxes : [],
            searchMetrics: searchMetrics,
            searchTier: searchMetrics.method_used || 'hybrid',
            searchTime: Math.round(backendTime)
          });
        } catch (error) {
          console.error('FindItems: Search error:', error);
          setError('Failed to search items. Please try again.');
          setSearchTier('keyword'); // Reset to default on error
          setSearchTime(null);
        } finally {
          setIsSearching(false);
          setIsLoading(false);
        }
      })();
    }
  }, [location.search, urlSearchProcessed, boxes]); // Re-run when URL search params change

  // Handle browser back button for modal navigation
  useEffect(() => {
    const handlePopState = (event) => {
      console.log('FindItems: Browser back button pressed');
      
      // Check if any modal is open and close it
      if (boxDetailOpen) {
        console.log('FindItems: Closing box modal due to browser back');
        setBoxDetailOpen(false);
        setSelectedBoxId(null);
        event.preventDefault();
        return;
      }
      
      if (itemDetailOpen) {
        console.log('FindItems: Closing item modal due to browser back');
        setItemDetailOpen(false);
        setSelectedItemId(null);
        event.preventDefault();
        return;
      }
    };

    // Add event listener for browser back button
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [boxDetailOpen, itemDetailOpen]);

  
  // Online-only mode - removed loadFromOfflineStorage function
  
  // Check for data updates from localStorage
  const checkForDataRefresh = useCallback(() => {
    console.log('Checking for data refresh signals...');
    
    // Check for our new specific item deletion signal first - this is the most explicit trigger
    const itemsDeleted = localStorage.getItem('cubxly_items_deleted');
    const deletedFromBox = localStorage.getItem('cubxly_items_deleted_from_box');
    
    if (itemsDeleted && deletedFromBox) {
      console.log(`CRITICAL EVENT: Items were deleted from box ${deletedFromBox} at ${new Date(parseInt(itemsDeleted, 10)).toLocaleTimeString()}`);
      console.log('Forcing complete data refresh due to item deletion...');
      
      // Items were deleted - always do a complete refresh to ensure accurate counts
      fetchData();
      
      // Clear these signals
      localStorage.removeItem('cubxly_items_deleted');
      localStorage.removeItem('cubxly_items_deleted_from_box');
      return; // Exit early since we've already refreshed everything
    }
    
    // Check if items were added (e.g., from multi-item image cropping)
    const itemsAdded = localStorage.getItem('cubxly_items_added');
    if (itemsAdded) {
      console.log(`Items were added at ${new Date(parseInt(itemsAdded, 10)).toLocaleTimeString()}`);
      console.log('Refreshing all data to get accurate box item counts after item addition...');
      
      // Items were added - do a complete refresh to ensure accurate counts
      fetchData();
      
      // Clear the signal
      localStorage.removeItem('cubxly_items_added');
      localStorage.removeItem('cubxly_box_updated');
      localStorage.removeItem('cubxly_force_refresh');
      return; // Exit early
    }
    
    // Check if a specific box was updated - secondary priority
    const updatedBoxId = localStorage.getItem('cubxly_box_updated');
    const updatedItemCount = localStorage.getItem('cubxly_box_item_count');
    
    if (updatedBoxId && updatedItemCount) {
      console.log(`Box ${updatedBoxId} was updated to have ${updatedItemCount} items`);
      console.log('Refreshing all data to get accurate box item counts');
      
      // Do a complete refresh to ensure everything is in sync
      fetchData();
      
      // Clear the update signals
      localStorage.removeItem('cubxly_box_updated');
      localStorage.removeItem('cubxly_box_item_count');
      return; // Exit early
    }
    
    // Check if box was updated without item count (e.g., from multi-item cropping)
    const boxUpdated = localStorage.getItem('cubxly_box_updated');
    const forceRefresh = localStorage.getItem('cubxly_force_refresh');
    if (boxUpdated && forceRefresh) {
      console.log(`Box ${boxUpdated} was updated, forcing refresh...`);
      fetchData();
      localStorage.removeItem('cubxly_box_updated');
      localStorage.removeItem('cubxly_force_refresh');
      return;
    }
    
    // Check for general refresh signal - lowest priority
    const lastRefresh = localStorage.getItem('cubxly_data_refresh');
    if (lastRefresh) {
      console.log('General data refresh detected, reloading all data...');
      fetchData();
      // Clear the refresh flag after processing
      localStorage.removeItem('cubxly_data_refresh');
    }
  }, [fetchData]);
  
  // Set up listeners for storage events and visibility changes
  const handleStorageChange = useCallback((e) => {
    if (e.key === 'cubxly_data_refresh' || 
        e.key === 'cubxly_items_added' || 
        e.key === 'cubxly_force_refresh' ||
        e.key === 'cubxly_box_updated') {
      checkForDataRefresh();
    }
  }, [checkForDataRefresh]);
  
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      console.log('Page became visible, checking for data updates...');
      checkForDataRefresh();
    }
  }, [checkForDataRefresh]);
  
  // Listen for data refresh events
  useEffect(() => {
    // Check if we have a search query - if so, don't do the initial data fetch
    const urlParams = new URLSearchParams(location.search);
    const queryParam = urlParams.get('q');
    const tagSearchParam = urlParams.get('tagSearch');
    
    if ((!queryParam || !queryParam.trim()) && (!tagSearchParam || !tagSearchParam.trim())) {
      // Clear any stale box data in local storage
      localStorage.removeItem('cubxly_box_cache');
      
      // Force fresh data fetch with cache busting only if no search query
      fetchData(true); // Pass true to indicate this is a forced refresh
    } else {
      console.log('FindItems: Skipping data refresh useEffect due to search query or tag search');
    }
    
    // Set up event listeners for data changes
    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData, handleStorageChange, handleVisibilityChange, location.search]);

  // Handle basic search (keyword-based)
  const handleBasicSearch = async (queryOverride = null) => {
    const query = queryOverride || searchQuery;
    console.log('FindItems: handleBasicSearch called with query:', query, 'queryOverride:', queryOverride, 'searchQuery:', searchQuery);
    if (!query.trim()) {
      console.log('FindItems: handleBasicSearch - empty query, returning');
      return;
    }
    
    // Check cache first
    const cachedResults = getCachedSearch(query);
    if (cachedResults) {
      console.log('FindItems: ✅ Using cached search results');
      console.log('FindItems: Cached boxes:', cachedResults.boxes.map(b => ({
        id: b.id,
        name: b.name,
        searchItemsCount: b.searchItemsCount
      })));
      setItems(cachedResults.items);
      setBoxes(cachedResults.boxes);
      setSearchTier(cachedResults.searchTier);
      setSearchTime(cachedResults.searchTime);
      setLastSearchQuery(query);
      setShowAiSearchOption(true);
      return;
    }
    
    try {
      setIsSearching(true);
      setIsLoading(true);
      setShowAiSearchOption(false);
      setLastSearchQuery(query);
      
      // Record start time for performance metrics
      const startTime = performance.now();
      
      // Use enhanced hybrid search for all searches
      const endpoint = `/search?q=${encodeURIComponent(query)}`;
      console.log(`Using enhanced hybrid search endpoint: ${endpoint}`);
      
      const response = await api.get(endpoint);
      console.log('Basic search response:', response.data);
      console.log('API returned items:', response.data.items?.length);
      console.log('API returned boxes:', response.data.boxes?.length);
      
      // Calculate search execution time
      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);
      
      // Extract search metrics from enhanced hybrid search response
      const searchMetrics = response.data?.search_metrics || {};
      const backendTime = searchMetrics.total_time_ms || duration;
      setSearchTime(Math.round(backendTime));
      setSearchTier(searchMetrics.method_used || 'hybrid');
      
      // Log search metrics for debugging
      console.log('Search metrics:', {
        method_used: searchMetrics.method_used,
        total_time_ms: searchMetrics.total_time_ms,
        vector_results_count: searchMetrics.vector_results_count,
        llm_results_count: searchMetrics.llm_results_count,
        fallback_triggered: searchMetrics.fallback_triggered,
        top_similarity: searchMetrics.top_similarity
      });
      
      // Process search results
      let searchResults = [];
      
      // Handle different response formats
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array response
          searchResults = response.data;
        } else if (typeof response.data === 'object' && Array.isArray(response.data.items)) {
          // Object with items array
          searchResults = response.data.items;
        }
      }
      
      console.log(`Basic search found ${searchResults.length} items from backend`);
      console.log('First 3 items:', searchResults.slice(0, 3).map(i => ({ id: i.id, name: i.name, box_id: i.box_id })));
      
      // Display all items from backend in the order returned (highest match rank first)
      setItems(searchResults);
      
      // Always show AI search option after basic search
      setShowAiSearchOption(true);
      
      // Client-side filtering: Extract box IDs that contain ANY of the matching items
      const matchingBoxIds = new Set();
      searchResults.forEach(item => {
        if (item.box_id) {
          matchingBoxIds.add(item.box_id);
        }
      });
      
      // Declare matchingBoxes at function scope so it's available for caching
      let matchingBoxes = [];
      
      if (searchResults.length > 0) {
        console.log('Found search results:', searchResults.length, 'items');
        
        // Get all unique box IDs from search results
        const boxIdsInSearch = [];
        searchResults.forEach(item => {
          if (item.box_id && !boxIdsInSearch.includes(item.box_id)) {
            boxIdsInSearch.push(item.box_id);
          }
        });
        
        console.log('Unique box IDs in search results:', boxIdsInSearch);
        
        // Filter boxes that match the search results
        matchingBoxes = [];
        
        // Always fetch fresh box data during search to get complete box list
        // (boxes state may contain filtered results from previous search)
        console.log('Fetching complete box list for search results...');
        try {
          const boxResponse = await api.get('/boxes');
          const allBoxes = boxResponse.data || [];
          console.log(`Fetched ${allBoxes.length} boxes from API`);
          
          // Filter boxes that match the search results
          boxIdsInSearch.forEach(boxId => {
            const existingBox = allBoxes.find(b => b.id === boxId);
            console.log(`Looking for box ${boxId}: ${existingBox ? 'FOUND' : 'NOT FOUND'}`);
            
            if (existingBox) {
              // Count ALL items from this box in the search results
              const matchingItems = searchResults.filter(item => item.box_id === boxId);
              console.log(`Box ${boxId} has ${matchingItems.length} matching items`);
              
              // Add the box with updated counts
              matchingBoxes.push({
                ...existingBox,
                searchItems: matchingItems,
                searchItemsCount: matchingItems.length
              });
            } else {
              console.log(`⚠️ Box ${boxId} not found in API response`);
            }
          });
        } catch (boxError) {
          console.error('Error fetching boxes for search:', boxError);
        }
        
        console.log(`Found ${matchingBoxes.length} boxes containing search matches`);
        setBoxes(matchingBoxes);
      } else {
        // If no search results, show no boxes
        console.log('No search results found, showing no boxes');
        setBoxes([]);
      }
      
      // Save successful search to cache
      saveSearchToCache(query, {
        items: searchResults,
        boxes: matchingBoxes.length > 0 ? matchingBoxes : [],
        searchMetrics: searchMetrics,
        searchTier: searchMetrics.method_used || 'hybrid',
        searchTime: Math.round(backendTime)
      });
    } catch (error) {
      console.error('Search error:', error);
      setError('Failed to search items. Please try again.');
      setSearchTier('keyword'); // Reset to default on error
      setSearchTime(null);
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  // Handle AI search using hybrid endpoint
  const handleAiSearch = async () => {
    if (!lastSearchQuery) return;
    
    try {
      setIsSearching(true);
      setIsLoading(true);
      setShowAiSearchOption(false); // Hide AI option while AI search is in progress
      
      // Record start time for AI search
      const startTime = performance.now();
      
      // AI search uses the enhanced hybrid search endpoint  
      const endpoint = `/search?q=${encodeURIComponent(lastSearchQuery)}`;
      console.log(`Using enhanced hybrid search endpoint: ${endpoint}`);
      
      const response = await api.get(endpoint);
      console.log('AI search response:', response.data);
      
      // Calculate search execution time
      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);
      
      // Extract search metrics from enhanced hybrid search response
      const searchMetrics = response.data?.search_metrics || {};
      const backendTime = searchMetrics.total_time_ms || duration;
      setSearchTime(Math.round(backendTime));
      setSearchTier(searchMetrics.method_used || 'hybrid'); // Use actual method from backend
      
      // Log search metrics for debugging
      console.log('AI search metrics:', {
        method_used: searchMetrics.method_used,
        total_time_ms: searchMetrics.total_time_ms,
        vector_results_count: searchMetrics.vector_results_count,
        llm_results_count: searchMetrics.llm_results_count,
        fallback_triggered: searchMetrics.fallback_triggered,
        top_similarity: searchMetrics.top_similarity
      });
      
      // Process search results - hybrid search returns a structured object
      let searchResults = [];
      let matchingBoxes = [];
      
      if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data.items)) {
          searchResults = response.data.items;
        }
        
        // Get boxes from response if available
        if (Array.isArray(response.data.boxes)) {
          matchingBoxes = response.data.boxes;
          setBoxes(matchingBoxes);
        }
      }
      
      console.log(`AI search found ${searchResults.length} items and ${matchingBoxes.length} boxes`);
      setItems(searchResults);
      
      // Process boxes if not provided directly
      if (searchResults.length > 0 && matchingBoxes.length === 0) {
        // Process matching boxes similar to basic search
        const matchingBoxIds = new Set();
        searchResults.forEach(result => {
          // Handle both direct items and LLM search result format
          const item = result.item || result;
          if (item.box_id) {
            matchingBoxIds.add(item.box_id);
          }
        });
        
        if (matchingBoxIds.size > 0) {
          // Filter boxes that contain items from search results
          const filteredBoxes = boxes.filter(box => matchingBoxIds.has(box.id));
          const boxesWithUpdatedCounts = filteredBoxes.map(box => {
            const matchingItems = searchResults.filter(result => {
              const item = result.item || result;
              return item.box_id === box.id;
            });
            return {
              ...box,
              searchItems: matchingItems,
              searchItemsCount: matchingItems.length,
              items: box.items ? box.items.map(item => ({
                ...item,
                isSearchMatch: matchingItems.some(result => {
                  const matchedItem = result.item || result;
                  return matchedItem.id === item.id;
                })
              })) : []
            };
          });
          
          setBoxes(boxesWithUpdatedCounts);
        }
      }
    } catch (error) {
      console.error('AI search error:', error);
      setError('Failed to perform AI search. Please try again.');
      setSearchTier('keyword'); // Reset to basic on error
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  // Handle search input changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  // Handle search submission
  const handleSearchSubmit = (value) => {
    // If it's coming from the search bar, check value type
    // Otherwise, it's called from a form submit event
    let queryToSearch = searchQuery;
    
    if (typeof value === 'string') {
      setSearchQuery(value);
      queryToSearch = value;
    } else if (value && value.preventDefault) {
      value.preventDefault();
    }
    
    // Update URL with search query for proper back button behavior and cache restoration
    if (queryToSearch && queryToSearch.trim()) {
      const newUrl = `${window.location.pathname}?q=${encodeURIComponent(queryToSearch.trim())}`;
      window.history.pushState({ search: queryToSearch }, '', newUrl);
      console.log('FindItems: Updated URL with search query:', queryToSearch);
    }
    
    handleBasicSearch(); // Always start with basic search
    
    // Mark demo feature as completed if in demo mode
    if (demoService.isDemoMode()) {
      demoService.markFeatureCompleted('search');
    }
  };

  // Open box detail modal instead of navigating
  const handleBoxClick = (boxId) => {
    setSelectedBoxId(boxId);
    setBoxDetailOpen(true);
    // Push a history entry so browser back button can close the modal
    window.history.pushState({ modal: 'box', boxId: boxId }, '', window.location.href);
    console.log('FindItems: Opened box modal and pushed history entry');
  };

  // Handle closing the box detail modal
  const handleCloseBoxDetail = () => {
    console.log('FindItems: closing box modal');
    setBoxDetailOpen(false);
    setSelectedBoxId(null);
  };

  // Open item detail modal instead of navigating
  const handleItemClick = (itemId) => {
    setSelectedItemId(itemId);
    setItemDetailOpen(true);
    // Push a history entry so browser back button can close the modal
    window.history.pushState({ modal: 'item', itemId: itemId }, '', window.location.href);
    console.log('FindItems: Opened item modal and pushed history entry');
  };
  
  // Handle closing the item detail modal
  const handleCloseItemDetail = () => {
    console.log('FindItems: closing item modal');
    setItemDetailOpen(false);
    setSelectedItemId(null);
  };

  // View all boxes
  const handleViewAllBoxes = () => {
    navigate.to.ALL_BOXES();
  };
  
  // View all items
  const handleViewAllItems = () => {
    navigate.to.ALL_ITEMS();
  };

  // Create the search component for the header
  const headerSearchComponent = (
    <Box sx={{ flexGrow: 1, mx: 1 }}>
      <EnhancedSearchBar
        value={searchQuery}
        onChange={handleSearchChange}
        onClear={() => {
          setSearchQuery('');
          setItems([]);
          setBoxes([]);
          setLastSearchQuery('');
          setShowAiSearchOption(false);
          setSearchTier('keyword');
          setSearchTime(null);
          // Clear URL search parameter
          window.history.pushState({}, '', window.location.pathname);
          fetchData();
        }}
        onSubmit={handleSearchSubmit}
        searchTier={searchTier}
        searchTime={searchTime}
        showAiOption={showAiSearchOption}
        onAiSearchRequest={handleAiSearch}
        isSearching={isSearching}
        placeholder="Search for items or boxes..."
        elevation={0}
        sx={{ 
          backgroundColor: 'white',
          borderRadius: 1,
          border: '2px solid rgba(0, 0, 0, 0.1)',
          p: 0.5,
          '& .MuiInputBase-root': {
            color: 'rgba(0, 0, 0, 0.87)',
            height: 38,
            pl: 1
          },
          '& .MuiInputBase-input': {
            '&::placeholder': {
              color: 'rgba(0, 0, 0, 0.54)'
            }
          },
          '& .MuiSvgIcon-root': {
            color: 'rgba(0, 0, 0, 0.54)'
          },
          '&:hover': {
            border: '2px solid rgba(0, 0, 0, 0.23)'
          },
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
        }}
        variant="standard"
      />
    </Box>
  );

  return (
    <Layout 
      title="Find Items" 
      onBack={() => window.history.back()} 
      searchComponent={headerSearchComponent}
    >
      <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
        
        {/* Offline data handling */}
        {!navigator.onLine && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Button 
              variant="contained" 
              color="primary"
              fullWidth
              onClick={() => fetchData(true)}
            >
              Load Offline Data
            </Button>
            
            {/* Add a simple count display for debugging */}
            <Box sx={{ mt: 1, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                Diagnostic: {boxes.length} boxes, {items.length} items loaded
              </Typography>
            </Box>
          </Paper>
        )}
        
        {/* Main Content */}
        <Box sx={{ position: 'relative' }}>
          {isLoading ? (
            <LoadingOverlay />
          ) : error ? (
            <Alert severity="warning" sx={{ my: 2 }}>
              {error}
            </Alert>
          ) : (
            <>
              {/* Tag Search Indicator */}
              {activeTagSearch && (
                <Alert 
                  severity="info" 
                  icon={<LocalOfferIcon />}
                  sx={{ mb: 2 }}
                  action={
                    <Button 
                      color="inherit" 
                      size="small"
                      onClick={() => {
                        setActiveTagSearch(null);
                        setSearchQuery('');
                        setLastSearchQuery('');
                        window.history.pushState({}, '', window.location.pathname);
                        fetchData();
                      }}
                    >
                      Clear
                    </Button>
                  }
                >
                  Showing items tagged: <strong>"{activeTagSearch}"</strong> ({items.length} items found)
                </Alert>
              )}
              
              {/* Boxes Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" component="h2">
                    Boxes:
                  </Typography>
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={handleViewAllBoxes}
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    All Boxes ({boxes ? boxes.length : 0})
                  </Link>
                </Box>
                
                <Grid container spacing={2}>
                  {/* Tag Dashboard Utility Card */}
                  <Grid item xs={6} sm={4} md={3}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                      }}
                    >
                      <CardActionArea 
                        onClick={() => navigate.to.TAGS()}
                        sx={{ 
                          height: '100%', 
                          p: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 200
                        }}
                      >
                        <LocalOfferIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                        <Typography variant="h6" component="div" align="center" sx={{ fontWeight: 600 }}>
                          Tag Dashboard
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ mt: 1, opacity: 0.9 }}>
                          View and manage your tags
                        </Typography>
                      </CardActionArea>
                    </Card>
                  </Grid>
                  
                  {/* Box Cards */}
                  {boxes && boxes.length > 0 ? (
                    boxes.map((box) => (
                      <Grid item xs={6} sm={4} md={3} key={box.id}>
                        <Card sx={{ height: '100%' }}>
                          <CardActionArea 
                            onClick={() => handleBoxClick(box.id)}
                            sx={{ height: '100%', p: 1 }}
                          >
                            <BoxThumbnail boxId={box.id} height={120} />
                            <CardContent>
                              <Typography gutterBottom variant="h6" component="div" noWrap>
                                {box.name}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary">
                                {(() => {
                                  // If we're searching (check both searchQuery and lastSearchQuery for cache restoration)
                                  const isSearchActive = (searchQuery && searchQuery.trim() !== '') || lastSearchQuery;
                                  
                                  if (isSearchActive) {
                                    // Use searchItemsCount for showing matching items
                                    if (typeof box.searchItemsCount === 'number') {
                                      return `${box.searchItemsCount} matching item${box.searchItemsCount !== 1 ? 's' : ''}`;
                                    }
                                    
                                    // Fallback: count matching items directly
                                    if (items && Array.isArray(items)) {
                                      const matchingCount = items.filter(item => item.box_id === box.id).length;
                                      return `${matchingCount} matching item${matchingCount !== 1 ? 's' : ''}`;
                                    }
                                  }
                                  
                                  // Not searching - show total item count
                                  // First check presence of the items array
                                  if (box.items && Array.isArray(box.items)) {
                                    return `${box.items.length} item${box.items.length !== 1 ? 's' : ''}`;
                                  }
                                  // Then check explicit counts
                                  else if (box.items_count) {
                                    return `${box.items_count} item${box.items_count !== 1 ? 's' : ''}`;  
                                  }
                                  else if (typeof box.item_count === 'number') {
                                    return `${box.item_count} item${box.item_count !== 1 ? 's' : ''}`;  
                                  }
                                  // Last resort - if no count data is available, try to fetch it
                                  else {
                                    // Trigger async fetch of item count (won't affect current render)
                                    (async () => {
                                      try {
                                        // Try to get items using the boxService which has proper fallbacks
                                        const boxItems = await boxService.getBoxItems(box.id);
                                        if (boxItems?.length) {
                                          // Update this specific box with its item count
                                          setBoxes(prevBoxes => 
                                            prevBoxes.map(b => 
                                              b.id === box.id 
                                                ? {...b, items_count: boxItems.length, item_count: boxItems.length}
                                                : b
                                            )
                                          );
                                        }
                                      } catch (err) {
                                        console.warn(`Failed to fetch item count for box ${box.id}:`, err);
                                      }
                                    })();
                                    
                                    // Show loading indicator for now
                                    return 'Loading items...';
                                  }
                                })()}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Box sx={{ width: '100%', py: 2, textAlign: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        No boxes found
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Box>
              
              {/* Items Section */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" component="h2">
                    Items:
                  </Typography>
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={handleViewAllItems}
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    All Items ({items ? items.length : 0})
                  </Link>
                </Box>
                
                <Grid container spacing={1}>
                  {itemsToRender && itemsToRender.length > 0 ? (
                    itemsToRender.map((item) => (
                      <Grid item xs={4} sm={3} md={2} key={item.id}>
                        <Card sx={{ height: '100%' }}>
                          <CardActionArea 
                            onClick={() => handleItemClick(item.id)}
                            sx={{ height: '100%' }}
                          >
                            <Box 
                              sx={{
                                height: 0,
                                paddingTop: '100%', // 1:1 aspect ratio
                                position: 'relative',
                                backgroundColor: 'rgba(0,0,0,0.04)'
                              }}
                            >
                              <OfflineImage
                                src={getBestItemImageUrl(item)}
                                itemId={item.id}
                                alt={item.name || 'Item'}
                                fallbackSrc={placeholderImage}
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </Box>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Box sx={{ width: '100%', py: 4, textAlign: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        No items found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Try different search terms or browse through boxes
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Box>
            </>
          )}
        </Box>
      </Box>
      
      {/* Item Detail Modal */}
      <ItemDetailModal 
        open={itemDetailOpen} 
        onClose={handleCloseItemDetail} 
        itemId={selectedItemId} 
        fromPage="find"
        onItemsAdded={(newItems) => {
          console.log(`${newItems.length} additional items created from group photo, refreshing data...`);
          fetchData();
        }}
      />
      
      {/* Box Detail Modal */}
      <BoxDetailModal
        open={boxDetailOpen}
        onClose={handleCloseBoxDetail}
        boxId={selectedBoxId}
      />
    </Layout>
  );
};

export default FindItems;
